import _extends from '@babel/runtime/helpers/extends';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import { CheckBox } from '@manulife/mux-cds-icons';
import PropTypes from '../../../node_modules/prop-types/index.js';
import Label from '../Label/Label.js';
import { safeRest, rem, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { CheckboxWrapper, CheckboxInput, IconWrapper, ChildrenWrapper } from './styledComponents.js';
import useTheme from '../../hooks/useTheme.js';
import useDerivedState from '../../hooks/useDerivedState.js';

var _excluded = ["checked", "children", "defaultChecked", "customStyle", "disabled", "error", "id", "onChange"];
var Checkbox = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var theme = useTheme();
  var controlledChecked = props.checked,
    children = props.children,
    defaultChecked = props.defaultChecked,
    customStyle = props.customStyle,
    disabled = props.disabled,
    error = props.error,
    id = props.id,
    _onChange = props.onChange,
    rest = _objectWithoutProperties(props, _excluded);
  var _useDerivedState = useDerivedState(defaultChecked, controlledChecked),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 2),
    checked = _useDerivedState2[0],
    setChecked = _useDerivedState2[1];
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle({
      checkboxStyle: _objectSpread({
        display: 'inline-block',
        position: 'relative'
      }, customStyle.checkboxStyle),
      labelStyle: customStyle.labelStyle
    }),
    checkboxStyle = _safeCustomStyle.checkboxStyle,
    labelStyle = _safeCustomStyle.labelStyle;

  // TODO: Update to CDS specs in V4: https://github.com/manulife-innersource/mux-core/issues/2437
  var iconStyle = {
    backgroundColor: disabled ? theme.colorPalette.neutralColors.light.default : theme.colorPalette.neutralColors.light.shade_white,
    color: error ? theme.colorPalette.utilityColors.error : disabled ? theme.colorPalette.neutralColors.light.shade_dark_3 : theme.colorPalette.primaryColors.main.shade_default,
    fontSize: rem(28)
  };

  // If text labels are passed as children
  if (children && typeof children === 'string') {
    return /*#__PURE__*/React.createElement(CheckboxWrapper, {
      as: Label
      // NOTE: This needs to be `customStyle` instead of `checkboxStyle` as we're passing it down to the Label
      ,
      customStyle: {
        labelStyle: _objectSpread(_objectSpread({}, customStyle.checkboxStyle), {}, {
          display: 'inline-flex',
          fontWeight: checked ? 400 : 300,
          lineHeight: rem(28),
          position: 'relative'
        })
      },
      isDisabled: disabled,
      hasChildren: !!children,
      htmlFor: id,
      showPointer: !disabled
    }, /*#__PURE__*/React.createElement(CheckboxInput, _extends({
      type: "checkbox",
      checked: checked,
      disabled: disabled,
      error: error,
      id: id,
      ref: ref,
      theme: theme,
      onChange: function onChange(e) {
        setChecked(e.target.checked);
        return handle(_onChange)(e);
      }
    }, safeProps)), /*#__PURE__*/React.createElement(IconWrapper, {
      className: "checkbox-icon"
    }, /*#__PURE__*/React.createElement(CheckBox, {
      style: iconStyle
    })), /*#__PURE__*/React.createElement(ChildrenWrapper, {
      childrenStyle: labelStyle
    }, children));
  }

  // No children, or children are custom elements
  return /*#__PURE__*/React.createElement(CheckboxWrapper, {
    checkboxStyle: checkboxStyle,
    hasChildren: !!children
  }, /*#__PURE__*/React.createElement(CheckboxInput, _extends({}, safeProps, {
    type: "checkbox",
    checked: checked,
    disabled: disabled,
    error: error,
    id: id,
    ref: ref,
    theme: theme,
    onChange: function onChange(e) {
      setChecked(e.target.checked);
      return handle(_onChange)(e);
    }
  })), /*#__PURE__*/React.createElement(IconWrapper, {
    className: "checkbox-icon"
  }, /*#__PURE__*/React.createElement(CheckBox, {
    style: iconStyle
  })), children ? /*#__PURE__*/React.createElement(ChildrenWrapper, {
    childrenStyle: labelStyle
  }, children) : null);
});
process.env.NODE_ENV !== "production" ? Checkbox.propTypes = {
  /** ID of the checkbox */
  id: PropTypes.string.isRequired,
  /** Boolean value indicating whether or not this checkbox is checked (as a controlled component) */
  checked: PropTypes.bool,
  /** Boolean value indicating whether or not this checkbox is checked by default (as an uncontrolled component) */
  defaultChecked: PropTypes.bool,
  /** Disable the checkbox */
  disabled: PropTypes.bool,
  /** Display checkbox error state */
  error: PropTypes.bool,
  /** Callback to be called when the checkbox changes */
  onChange: PropTypes.func,
  /** Style overrides */
  customStyle: PropTypes.shape({
    checkboxStyle: PropTypes.object,
    labelStyle: PropTypes.object
  })
} : void 0;
Checkbox.defaultProps = {
  checked: undefined,
  defaultChecked: false,
  disabled: false,
  error: false,
  onChange: noop,
  customStyle: {
    checkboxStyle: {},
    labelStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? Checkbox.displayName = 'Checkbox' : void 0;
var Checkbox$1 = Checkbox;

export { Checkbox$1 as default };
